import { Link } from 'react-router';
import { formatDatum, formatFt, formatMillioSav } from '../lib/formaz.ts';
import { MEGBIZAS_TIPUS_CIMKE } from '../lib/tipusok.ts';
import { Blokk, Tabla, UresSor } from './Blokk.tsx';
import { tipusCimke } from './cimkek.ts';
import type { EladasokValasz, EladasSor } from './tipusok.ts';
import { elteresSzoveg } from './valtozas.ts';

function ingatlanCim(e: EladasSor): string {
  return [e.iranyitoszam, e.terulet, e.utca].filter(Boolean).join(', ');
}

/** Az eladási ár eltérése a becslés közepétől; szakértői (szám nélküli) becslésnél null. */
function elteres(e: EladasSor): number | null {
  return e.becsles_kozep ? (e.tenyleges_ar - e.becsles_kozep) / e.becsles_kozep : null;
}

function Szam({ cimke, ertek, alcim }: { cimke: string; ertek: string; alcim?: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-[13px] text-szoveg-halk">{cimke}</dt>
      <dd className="szam-nagy mt-1 wrap-anywhere text-[22px] sm:text-[26px]">{ertek}</dd>
      {alcim && <dd className="mt-0.5 text-[13px] text-szoveg-halk">{alcim}</dd>}
    </div>
  );
}

/**
 * Megbízások és eladások a rögzítés napja szerint (az `elemzes_eladasok` adatbázis-függvény válasza). A többi blokkal
 * ellentétben nem a lead érkezése számít: az eladás jellemzően hónapokkal később történik.
 */
export default function EladasokBlokk({ d, tobbIroda }: { d: EladasokValasz; tobbIroda: boolean }) {
  const megbizasDb = d.megbizasok.kizarolagos + d.megbizasok.nem_kizarolagos;
  const fej = [
    { cim: 'Ingatlan' }, ...(tobbIroda ? [{ cim: 'Iroda' }] : []), { cim: 'Kolléga' }, { cim: 'Eladva' }, { cim: 'Megbízás' },
    { cim: 'Becsült sáv', j: true }, { cim: 'Eladási ár', j: true }, { cim: 'Eltérés', j: true },
  ];
  return (
    <Blokk cim="Megbízások és eladások" leiras="A rögzítés napja szerint, a lead érkezésétől függetlenül: az időszakban felvett megbízások és lezárt eladások.">
      {megbizasDb === 0 && d.db === 0 ? <UresSor szoveg="Ebben az időszakban nem rögzítettek megbízást vagy eladást." /> : (
        <>
          <dl aria-label="Megbízások és eladások összesítve" className="mt-4 grid grid-cols-2 gap-6 xl:grid-cols-4">
            <Szam cimke="Megbízás" ertek={String(megbizasDb)} alcim={`${d.megbizasok.kizarolagos} kizárólagos, ${d.megbizasok.nem_kizarolagos} nem kizárólagos`} />
            <Szam cimke="Eladott ingatlan" ertek={String(d.db)} />
            <Szam cimke="Összes eladási ár" ertek={formatFt(d.osszeg)} />
            <Szam cimke="Átlagos eltérés a becsléstől" ertek={elteresSzoveg(d.elteres_atlag)}
              alcim={d.becsult_db > 0 ? `${d.savon_belul_db} / ${d.becsult_db} a becsült sávon belül` : undefined} />
          </dl>
          {d.lista.length > 0 && (
            <Tabla nev="Eladott ingatlanok" minSzel={tobbIroda ? 920 : 800} fej={fej}>
              {d.lista.map((e) => (
                <tr key={e.lead_id}>
                  <td>
                    <Link to={`/leadek/${e.lead_id}`} className="block text-kiemelt underline">{ingatlanCim(e)}</Link>
                    <small className="block text-xs text-szoveg-halk">{tipusCimke(e.tipus)}</small>
                  </td>
                  {tobbIroda && <td>{e.iroda}</td>}
                  <td>{e.kollega ?? '–'}</td>
                  <td className="szam">{formatDatum(e.eladva_at)}</td>
                  <td>{e.megbizas_tipus ? MEGBIZAS_TIPUS_CIMKE[e.megbizas_tipus] ?? e.megbizas_tipus : '–'}</td>
                  <td className="j szam">{e.becsles_min && e.becsles_max ? formatMillioSav(e.becsles_min, e.becsles_max) : 'szakértői'}</td>
                  <td className="j szam font-medium">{formatFt(e.tenyleges_ar)}</td>
                  <td className="j szam">{elteresSzoveg(elteres(e))}</td>
                </tr>
              ))}
            </Tabla>
          )}
          {d.db > d.lista.length && <p className="mt-3 text-[13px] text-szoveg-halk">A legutóbbi {d.lista.length} eladás látszik a {d.db}-ból.</p>}
        </>
      )}
    </Blokk>
  );
}
